import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import { getPageSeo } from '../utils/pageSeo';
import '../styles/Platform.css';
import platformArchDark from '../assets/platform-architecture-dark.svg';
import platformArchLight from '../assets/platform-architecture-light.svg';

/* Returns the translated array or empty array fallback */
const tArr = (t, key) => {
  const val = t(key, { returnObjects: true });
  return Array.isArray(val) ? val : [];
};

/* ─── constants ─────────────────────────────────────────── */
const TAB_KEYS = ['dataIntegration', 'knowledge', 'agents', 'investigation', 'governance'];
const STEP_KEYS = ['assess', 'structure', 'deploy', 'govern', 'improve'];
const FLYWHEEL_KEYS = ['deploy', 'interact', 'capture', 'analyze', 'reinforce'];

/* Icon per flywheel stage - defined in JS, not locale */
const FLYWHEEL_ICONS = {
  deploy:    'fa-rocket',
  interact:  'fa-comments',
  capture:   'fa-satellite-dish',
  analyze:   'fa-magnifying-glass-chart',
  reinforce: 'fa-rotate',
};

/* Compute x/y unit vectors for 5 nodes evenly around a circle, starting from top */
const FLYWHEEL_POSITIONS = FLYWHEEL_KEYS.map((_, i) => {
  const angle = (-Math.PI / 2) + (i * 2 * Math.PI) / FLYWHEEL_KEYS.length;
  return { x: Math.cos(angle), y: Math.sin(angle) };
});


const Platform = () => {
  const { t } = useTranslation('platform');
  const { language } = useLanguage();
  const { theme } = useTheme();
  const seo = getPageSeo('platform', language, t);
  const [activeTab, setActiveTab] = useState(TAB_KEYS[0]);
  const [activeStep, setActiveStep] = useState(STEP_KEYS[0]);
  const [activeFlywheel, setActiveFlywheel] = useState(FLYWHEEL_KEYS[0]);
  // mobile accordion for deployment steps
  const [openMobileStep, setOpenMobileStep] = useState(STEP_KEYS[0]);

  const handleStepKey = (e, key) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveStep(key); }
  };
  const handleTabKey = (e, key) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveTab(key); }
  };
  const toggleMobileStep = (key) => {
    setOpenMobileStep((prev) => (prev === key ? null : key));
  };


  return (
    <div className="platform-page">
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath={`/${language}/platform`}
      />

      {/* ── Hero ───────────────────────────────────────────── */}
      <section className="hero-section platform-hero">
        <div className="hero-background">
          <div className="gradient-layer"></div>
          <div className="particle-layer"></div>
          <div className="grid-overlay"></div>
          <div className="tech-lines"></div>
        </div>
        <div className="hero-content">
          <div className="container">
            <div className="hero-wrapper">
              <div className="tech-element tech-element-1"></div>
              <div className="tech-element tech-element-4"></div>
              <div className="tech-element tech-element-7"></div>
              <span className="platform-hero-tag fade-in">{t('hero.tag')}</span>
              <h1 className="hero-quote fade-in" style={{ animationDelay: '0.1s' }}>
                <span className="quote-highlight">{t('hero.title')}</span>
              </h1>
              <p className="hero-subtitle fade-in" style={{ animationDelay: '0.2s' }}>
                {t('hero.subtitle')}
              </p>
              <Link
                to={`/${language}/contact`}
                className="platform-hero-cta fade-in"
                style={{ animationDelay: '0.35s' }}
              >
                {t('cta.button')}
                <i className="fas fa-arrow-right"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Challenge ──────────────────────────────────────── */}
      <section className="platform-section platform-challenge">
        <div className="container">
          <span className="platform-section-tag fade-in">{t('challenge.tag')}</span>
          <div className="platform-challenge-grid fade-in" style={{ animationDelay: '0.1s' }}>
            <div className="platform-challenge-stats">
              {['stat1', 'stat2', 'stat3'].map((key) => (
                <div key={key} className="platform-stat-card">
                  <span className="platform-stat-value">{t(`challenge.${key}.value`)}</span>
                  <span className="platform-stat-label">{t(`challenge.${key}.label`)}</span>
                </div>
              ))}
            </div>
            <div className="platform-challenge-text">
              <h2 className="platform-section-title">{t('challenge.title')}</h2>
              <p>{t('challenge.body1')}</p>
              <p>{t('challenge.body2')}</p>
              <p>{t('challenge.body3')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Architecture ───────────────────────────────────── */}
      <section className="platform-section platform-architecture">
        <div className="container">
          <span className="platform-section-tag fade-in">{t('architecture.tag')}</span>
          <h2 className="platform-section-title text-center fade-in" style={{ animationDelay: '0.1s' }}>
            {t('architecture.title')}
          </h2>
          <p className="platform-section-subtitle text-center fade-in" style={{ animationDelay: '0.15s' }}>
            {t('architecture.subtitle')}
          </p>

          <div className="platform-arch-svg-compare fade-in" style={{ animationDelay: '0.2s' }}>
            <img src={theme === 'light' ? platformArchLight : platformArchDark} alt="Unified Platform Architecture" className="platform-arch-svg-img"/>
          </div>
        </div>
      </section>

      {/* ── Platform Capabilities (tabbed) ─────────────────── */}
      <section className="platform-section platform-capabilities">
        <div className="container">
          <span className="platform-section-tag fade-in">{t('capabilities.tag')}</span>
          <h2 className="platform-section-title fade-in" style={{ animationDelay: '0.1s' }}>
            {t('capabilities.title')}
          </h2>
          <p className="platform-section-subtitle fade-in" style={{ animationDelay: '0.15s' }}>
            {t('capabilities.subtitle')}
          </p>

          <div className="platform-caps-layout fade-in" style={{ animationDelay: '0.2s' }}>
            {/* Tab list */}
            <div className="platform-caps-tabs" role="tablist">
              {TAB_KEYS.map((key) => (
                <button
                  key={key}
                  role="tab"
                  aria-selected={activeTab === key}
                  aria-controls={`tabpanel-${key}`}
                  id={`tab-${key}`}
                  className={`platform-cap-tab${activeTab === key ? ' active' : ''}`}
                  onClick={() => setActiveTab(key)}
                  onKeyDown={(e) => handleTabKey(e, key)}
                >
                  <span className="platform-cap-tab-label">{t(`capabilities.tabs.${key}.label`)}</span>
                  <i className="fas fa-chevron-right platform-cap-tab-chevron"></i>
                </button>
              ))}
            </div>

            {/* Tab panel */}
            <div className="platform-caps-panel">
              {TAB_KEYS.map((key) => (
                <div
                  key={key}
                  id={`tabpanel-${key}`}
                  role="tabpanel"
                  aria-labelledby={`tab-${key}`}
                  className={`platform-cap-content${activeTab === key ? ' active' : ''}`}
                >
                  <span className="platform-cap-tag">{t(`capabilities.tabs.${key}.tag`)}</span>
                  <h3 className="platform-cap-title">{t(`capabilities.tabs.${key}.title`)}</h3>
                  <p className="platform-cap-desc">{t(`capabilities.tabs.${key}.description`)}</p>
                  <ul className="platform-cap-list">
                    {tArr(t, `capabilities.tabs.${key}.items`).map((item, i) => (
                      <li key={i}><i className="fas fa-check"></i>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Deployment (step explorer) ──────────────────────── */}
      <section id="deployment" className="platform-section platform-deployment">
        <div className="container">
          <span className="platform-section-tag fade-in">{t('deployment.tag')}</span>
          <h2 className="platform-section-title fade-in" style={{ animationDelay: '0.1s' }}>
            {t('deployment.title')}
          </h2>
          <p className="platform-section-subtitle fade-in" style={{ animationDelay: '0.15s' }}>
            {t('deployment.subtitle')}
          </p>

          {/* Desktop stepper layout */}
          <div className="platform-deploy-layout fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="platform-deploy-steps" role="tablist" aria-label="Deployment steps">
              {STEP_KEYS.map((key, idx) => (
                <button
                  key={key}
                  role="tab"
                  aria-selected={activeStep === key}
                  aria-controls={`step-panel-${key}`}
                  id={`step-btn-${key}`}
                  className={`platform-deploy-step${activeStep === key ? ' active' : ''}`}
                  onClick={() => setActiveStep(key)}
                  onKeyDown={(e) => handleStepKey(e, key)}
                >
                  <span className="deploy-step-number">{t(`deployment.steps.${key}.number`)}</span>
                  <div className="deploy-step-text">
                    <span className="deploy-step-label">{t(`deployment.steps.${key}.label`)}</span>
                    <span className="deploy-step-subtitle">{t(`deployment.steps.${key}.subtitle`)}</span>
                  </div>
                  {idx < STEP_KEYS.length - 1 && (
                    <div className={`deploy-step-connector${activeStep === key ? ' active' : ''}`}></div>
                  )}
                </button>
              ))}
            </div>

            {/* Desktop right panel */}
            <div className="platform-deploy-panel">
              {STEP_KEYS.map((key) => (
                <div
                  key={key}
                  id={`step-panel-${key}`}
                  role="tabpanel"
                  aria-labelledby={`step-btn-${key}`}
                  className={`platform-deploy-content${activeStep === key ? ' active' : ''}`}
                >
                  <span className="platform-cap-tag">{t(`deployment.steps.${key}.label`)}</span>
                  <h3 className="platform-deploy-title">{t(`deployment.steps.${key}.title`)}</h3>
                  <p>{t(`deployment.steps.${key}.description1`)}</p>
                  <p>{t(`deployment.steps.${key}.description2`)}</p>
                  <div className="platform-deploy-activities">
                    <span className="platform-activities-label">{t('deployment.keyActivities')}</span>
                    <ul className="platform-cap-list">
                      {tArr(t, `deployment.steps.${key}.items`).map((item, i) => (
                          <li key={i}><i className="fas fa-check"></i>{item}</li>
                        ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile accordion layout */}
          <div className="platform-deploy-mobile">
            {STEP_KEYS.map((key) => {
              const isOpen = openMobileStep === key;
              return (
                <div key={key} className={`platform-mobile-step${isOpen ? ' open' : ''}`}>
                  <button
                    className="platform-mobile-step-header"
                    onClick={() => toggleMobileStep(key)}
                    aria-expanded={isOpen}
                  >
                    <span className="deploy-step-number">{t(`deployment.steps.${key}.number`)}</span>
                    <div className="deploy-step-text">
                      <span className="deploy-step-label">{t(`deployment.steps.${key}.label`)}</span>
                      <span className="deploy-step-subtitle">{t(`deployment.steps.${key}.subtitle`)}</span>
                    </div>
                    <i className={`fas fa-chevron-down mobile-step-chevron${isOpen ? ' rotated' : ''}`}></i>
                  </button>
                  <div className="platform-mobile-step-body">
                    <span className="platform-cap-tag">{t(`deployment.steps.${key}.label`)}</span>
                    <h3 className="platform-deploy-title">{t(`deployment.steps.${key}.title`)}</h3>
                    <p>{t(`deployment.steps.${key}.description1`)}</p>
                    <p>{t(`deployment.steps.${key}.description2`)}</p>
                    <div className="platform-deploy-activities">
                      <span className="platform-activities-label">{t('deployment.keyActivities')}</span>
                      <ul className="platform-cap-list">
                        {tArr(t, `deployment.steps.${key}.items`).map((item, i) => (
                          <li key={i}><i className="fas fa-check"></i>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── AI Feedback Flywheel ───────────────────────────── */}
      <section className="platform-section platform-flywheel">
        <div className="container">
          <span className="platform-section-tag fade-in">{t('flywheel.tag')}</span>
          <h2 className="platform-section-title text-center fade-in" style={{ animationDelay: '0.1s' }}>
            {t('flywheel.title')}
          </h2>
          <p className="platform-section-subtitle text-center fade-in" style={{ animationDelay: '0.15s' }}>
            {t('flywheel.subtitle')}
          </p>

          <div className="flywheel-layout fade-in" style={{ animationDelay: '0.2s' }}>
            {/* Circular diagram */}
            <div className="flywheel-diagram" aria-label="AI Feedback Flywheel diagram">
              {/* Animated spinning ring */}
              <div className="flywheel-ring-track"></div>
              <div className="flywheel-ring-spin"></div>

              {/* Center hub */}
              <div className="flywheel-hub">
                <i className="fas fa-infinity flywheel-hub-icon"></i>
                <span>{t('flywheel.hub')}</span>
              </div>

              {/* Stage nodes */}
              {FLYWHEEL_KEYS.map((key, i) => {
                const pos = FLYWHEEL_POSITIONS[i];
                const isActive = activeFlywheel === key;
                return (
                  <button
                    key={key}
                    className={`flywheel-node${isActive ? ' active' : ''}`}
                    style={{ '--fw-x': pos.x, '--fw-y': pos.y }}
                    onClick={() => setActiveFlywheel(key)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveFlywheel(key); } }}
                    aria-pressed={isActive}
                    aria-label={t(`flywheel.stages.${key}.label`)}
                  >
                    <div className="flywheel-node-inner">
                      <i className={`fas ${FLYWHEEL_ICONS[key]} flywheel-node-icon`}></i>
                      <span className="flywheel-node-label">{t(`flywheel.stages.${key}.label`)}</span>
                    </div>
                  </button>
                );
              })}

              {/* Flow arrows between nodes (decorative SVG) */}
              <svg className="flywheel-arrows" viewBox="0 0 400 400" aria-hidden="true">
                {FLYWHEEL_KEYS.map((_, i) => {
                  const a = FLYWHEEL_POSITIONS[i];
                  const b = FLYWHEEL_POSITIONS[(i + 1) % FLYWHEEL_KEYS.length];
                  const r = 150;
                  const mx = 200 + ((a.x + b.x) / 2) * r * 1.12;
                  const my = 200 + ((a.y + b.y) / 2) * r * 1.12;
                  return (
                    <path
                      key={i}
                      d={`M ${200 + a.x * r} ${200 + a.y * r} Q ${mx} ${my} ${200 + b.x * r} ${200 + b.y * r}`}
                      fill="none"
                      className="flywheel-arrow-path"
                      markerEnd="url(#fw-arrow)"
                    />
                  );
                })}
                <defs>
                  <marker id="fw-arrow" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                    <path d="M0,0 L0,6 L6,3 z" className="flywheel-arrow-marker" />
                  </marker>
                </defs>
              </svg>
            </div>

            {/* Detail card */}
            <div className="flywheel-detail">
              {FLYWHEEL_KEYS.map((key) => (
                <div
                  key={key}
                  className={`flywheel-detail-content${activeFlywheel === key ? ' active' : ''}`}
                >
                  <div className="flywheel-detail-header">
                    <div className="flywheel-detail-icon-wrap">
                      <i className={`fas ${FLYWHEEL_ICONS[key]}`}></i>
                    </div>
                    <div>
                      <span className="platform-cap-tag">{t(`flywheel.stages.${key}.label`)}</span>
                      <h3 className="flywheel-detail-title">{t(`flywheel.stages.${key}.subtitle`)}</h3>
                    </div>
                  </div>
                  <p className="flywheel-detail-desc">{t(`flywheel.stages.${key}.description`)}</p>
                  <div className="flywheel-signals">
                    <span className="platform-activities-label">{t('flywheel.signalsLabel')}</span>
                    <div className="flywheel-signal-chips">
                      {tArr(t, `flywheel.stages.${key}.signals`).map((signal, i) => (
                        <span key={i} className="flywheel-chip">{signal}</span>
                      ))}
                    </div>
                  </div>
                  {/* Stage nav dots */}
                  <div className="flywheel-nav-dots">
                    {FLYWHEEL_KEYS.map((k) => (
                      <button
                        key={k}
                        className={`flywheel-dot${activeFlywheel === k ? ' active' : ''}`}
                        onClick={() => setActiveFlywheel(k)}
                        aria-label={t(`flywheel.stages.${k}.label`)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────── */}
      <section className="platform-section platform-cta">
        <div className="container">
          <div className="platform-cta-inner fade-in">
            <span className="platform-section-tag">{t('cta.tag')}</span>
            <h2 className="platform-cta-title">{t('cta.title')}</h2>
            <p>{t('cta.body1')}</p>
            <p>{t('cta.body2')}</p>
            <Link to={`/${language}/contact`} className="platform-hero-cta">
              {t('cta.button')}
              <i className="fas fa-arrow-right"></i>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Platform;
